import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useRouter, useSearch } from '@tanstack/react-router';
import type { ImageType } from '@vizit/api';
import { queryKeys, queryUtils } from '@vizit/api';
import { AssetTableFilterOptions } from '@vizit/assets';
import { useAuth } from '@vizit/auth';
import { BenchmarkGridFilterOptions, CollectionGridFilterOptions, CollectionSortOption } from '@vizit/collections';
import { BenchmarkSortOption } from '@vizit/collections';
import { ScoreLevel } from '@vizit/organisms';
import { ProductTableFilterOptions } from '@vizit/products';
import * as React from 'react';
import { useActiveWorkspaceId } from '../../../../../hooks/use-workspace-link';
import { CollectionsViewOptions, LibraryPage, LibraryTabOptions, PagesProvider } from '../../../../../pages';
import {
  LIBRARY_BREADCRUMB,
  beforeLoadAuthenticatedValidation,
  getLibraryBreadcrumb,
  navigateWithState,
} from '../../../../../router/route-utils';
import { NavbarPageTemplate } from '../../../../../templates';

interface LibrarySearch {
  view: LibraryTabOptions;
  // Product table filters
  [ProductTableFilterOptions.ListingScoreLevel]?: ScoreLevel;
  [ProductTableFilterOptions.Brand]?: string;
  [ProductTableFilterOptions.ProductCategory]?: string;
  [ProductTableFilterOptions.ProductCategoryName]?: string;
  [ProductTableFilterOptions.Search]?: string;
  [ProductTableFilterOptions.Retailer]?: string;
  [ProductTableFilterOptions.Experiment]?: string;

  // Asset table filters
  [AssetTableFilterOptions.ImageType]?: string;
  [AssetTableFilterOptions.ProductCategory]?: string;
  [AssetTableFilterOptions.ProductCategoryName]?: string;

  // Collection grid filters
  [CollectionGridFilterOptions.ImageType]?: string;
  [CollectionGridFilterOptions.ProductCategory]?: string;
  [CollectionGridFilterOptions.ProductCategoryName]?: string;
  [CollectionGridFilterOptions.Search]?: string;
  [CollectionGridFilterOptions.Sort]?: CollectionSortOption;
  // Collections tab: Collections or Benchmarks view (only read while custom benchmarks are on)
  collectionsView?: CollectionsViewOptions;

  // Benchmarks view filters
  [BenchmarkGridFilterOptions.Search]?: string;
  [BenchmarkGridFilterOptions.ProductCategory]?: string;
  [BenchmarkGridFilterOptions.ProductCategoryName]?: string;
  [BenchmarkGridFilterOptions.Sort]?: BenchmarkSortOption;
}

function parseCollectionSortOption(value: unknown): CollectionSortOption | undefined {
  return value === CollectionSortOption.Alphabetical ? CollectionSortOption.Alphabetical : undefined;
}

function parseCollectionsView(value: unknown): CollectionsViewOptions | undefined {
  return value === CollectionsViewOptions.Benchmarks ? CollectionsViewOptions.Benchmarks : undefined;
}

function parseBenchmarkSort(value: unknown): BenchmarkSortOption | undefined {
  // Newest is the default, so it is never written to the URL.
  return value === BenchmarkSortOption.Oldest || value === BenchmarkSortOption.Alphabetical ? value : undefined;
}

function parseOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value ? value : undefined;
}

function parseCollectionImageType(value: unknown): ImageType | undefined {
  return value === 'hero' || value === 'carousel' ? value : undefined;
}

export const Route = createFileRoute('/_authenticated/w/$workspaceSlug/library/')({
  staticData: {
    pageName: 'Library',
  },
  component: RouteComponent,
  beforeLoad: ({ context, location }) => beforeLoadAuthenticatedValidation(context, location),
  validateSearch: (search: Record<string, unknown>): LibrarySearch => {
    const validValues = Object.values(LibraryTabOptions);

    const view =
      typeof search.view === 'string' && validValues.includes(search.view as LibraryTabOptions)
        ? (search.view as LibraryTabOptions)
        : LibraryTabOptions.Products;

    // Products tab supports these filters
    if (view === LibraryTabOptions.Products) {
      return {
        view,
        [ProductTableFilterOptions.ListingScoreLevel]:
          search.score && typeof search.score === 'string' && search.score !== ScoreLevel.All
            ? (search.score as ScoreLevel)
            : undefined,
        [ProductTableFilterOptions.Brand]: search.brand ? (search.brand as string) : undefined,
        [ProductTableFilterOptions.ProductCategory]: search.categoryId ? (search.categoryId as string) : undefined,
        [ProductTableFilterOptions.ProductCategoryName]: search.categoryName
          ? (search.categoryName as string)
          : undefined,
        [ProductTableFilterOptions.Search]: search.searchTerm ? (search.searchTerm as string) : undefined,
        [ProductTableFilterOptions.Retailer]: search.retailer ? (search.retailer as string) : undefined,
        [ProductTableFilterOptions.Experiment]:
          search.experiment === 'true' || search.experiment === true ? 'true' : undefined,
      };
    }

    // Assets tab supports these filters
    if (view === LibraryTabOptions.Assets) {
      return {
        view,
        [AssetTableFilterOptions.ImageType]: search.imageType ? (search.imageType as string) : undefined,
        [AssetTableFilterOptions.ProductCategory]: search.assetCategoryId
          ? (search.assetCategoryId as string)
          : undefined,
        [AssetTableFilterOptions.ProductCategoryName]: search.assetCategoryName
          ? (search.assetCategoryName as string)
          : undefined,
      };
    }

    if (view === LibraryTabOptions.Collections) {
      return {
        view,
        [CollectionGridFilterOptions.ImageType]: parseCollectionImageType(search.collectionImageType),
        [CollectionGridFilterOptions.ProductCategory]: search.collectionCategoryId
          ? (search.collectionCategoryId as string)
          : undefined,
        [CollectionGridFilterOptions.ProductCategoryName]: search.collectionCategoryName
          ? (search.collectionCategoryName as string)
          : undefined,
        [CollectionGridFilterOptions.Search]: search.collectionSearchTerm
          ? (search.collectionSearchTerm as string)
          : undefined,
        [CollectionGridFilterOptions.Sort]: parseCollectionSortOption(search.collectionSort),
        collectionsView: parseCollectionsView(search.collectionsView),
        [BenchmarkGridFilterOptions.Search]: parseOptionalString(search.benchmarkSearchTerm),
        [BenchmarkGridFilterOptions.ProductCategory]: parseOptionalString(search.benchmarkCategoryId),
        [BenchmarkGridFilterOptions.ProductCategoryName]: parseOptionalString(search.benchmarkCategoryName),
        [BenchmarkGridFilterOptions.Sort]: parseBenchmarkSort(search.benchmarkSort),
      };
    }

    return { view };
  },
});

function RouteComponent() {
  const router = useRouter();
  const search = useSearch({ from: Route.id });
  const queryClient = useQueryClient();
  const { organizationId } = useAuth();
  const { workspaceSlug } = Route.useParams();
  const workspaceId = useActiveWorkspaceId();

  const handleOnTabChange = React.useCallback(
    async (key: LibraryTabOptions) => {
      await router.navigate({
        to: Route.fullPath,
        search: (prev: Record<string, unknown>) => {
          const {
            view,
            score,
            brand,
            categoryId,
            categoryName,
            searchTerm,
            searchBy,
            experiment,
            imageType,
            assetCategoryId,
            assetCategoryName,
            collectionCategoryId,
            collectionCategoryName,
            collectionImageType,
            collectionSearchTerm,
            collectionSort,
            collectionsView,
            benchmarkSearchTerm,
            benchmarkCategoryId,
            benchmarkCategoryName,
            benchmarkSort,
            ...rest
          } = prev;

          if (key === LibraryTabOptions.Products) {
            return {
              ...rest,
              view: key,
              score: score ? (score as ScoreLevel) : undefined,
              brand: brand ? (brand as string) : undefined,
              categoryId: categoryId ? (categoryId as string) : undefined,
              categoryName: categoryName ? (categoryName as string) : undefined,
              searchTerm: searchTerm ? (searchTerm as string) : undefined,
              searchBy: searchBy ? (searchBy as string) : undefined,
              experiment: experiment ? 'true' : undefined,
            };
          }

          if (key === LibraryTabOptions.Assets) {
            return {
              ...rest,
              view: key,
              assetCategoryId: assetCategoryId ? (assetCategoryId as string) : undefined,
              assetCategoryName: assetCategoryName ? (assetCategoryName as string) : undefined,
              imageType: imageType ? (imageType as string) : undefined,
            };
          }

          if (key === LibraryTabOptions.Collections) {
            return {
              ...rest,
              view: key,
              collectionCategoryId: collectionCategoryId ? (collectionCategoryId as string) : undefined,
              collectionCategoryName: collectionCategoryName ? (collectionCategoryName as string) : undefined,
              collectionImageType: collectionImageType ? (collectionImageType as string) : undefined,
              collectionSearchTerm: collectionSearchTerm ? (collectionSearchTerm as string) : undefined,
              collectionSort: collectionSort ? (collectionSort as CollectionSortOption) : undefined,
              collectionsView: parseCollectionsView(collectionsView),
              benchmarkSearchTerm: parseOptionalString(benchmarkSearchTerm),
              benchmarkCategoryId: parseOptionalString(benchmarkCategoryId),
              benchmarkCategoryName: parseOptionalString(benchmarkCategoryName),
              benchmarkSort: parseBenchmarkSort(benchmarkSort),
            };
          }

          return {
            ...rest,
            view: key,
          };
        },
      });
    },
    [router],
  );

  const handleOnSuccessfulUpload = (assetId: string) => {
    navigateWithState(router, {
      to: '/w/$workspaceSlug/library/assets/$assetId',
      params: { workspaceSlug, assetId },
      from: { trail: [getLibraryBreadcrumb('assets')] },
      search: (prev: Record<string, unknown>) => ({ ...prev, view: 'assets' }),
    });

    queryClient.invalidateQueries({
      queryKey: queryKeys.images.infinite({
        workspace_id: workspaceId ?? '',
        limit: 12,
        sort_by: 'created_at',
        sort_direction: 'desc',
        parent_id: undefined,
        source: undefined,
        exclude_source: ['generated'],
      }).queryKey,
    });
  };

  const handleOnSuccessfulImport = (pdpId: string) => {
    navigateWithState(router, {
      to: '/w/$workspaceSlug/library/products/$pdpId',
      params: { workspaceSlug, pdpId },
      from: { trail: [LIBRARY_BREADCRUMB] },
      search: (prev: Record<string, unknown>) => ({ ...prev, view: 'products' }),
    });

    queryUtils.invalidateAllProducts(queryClient);
  };

  const handleOnProductTableFilterChange = (filter: ProductTableFilterOptions, value?: string) => {
    navigateWithState(router, {
      to: Route.fullPath,
      search: (prev: Record<string, unknown>) => ({ ...prev, [filter]: value }),
    });
  };

  const handleOnAssetTableFilterChange = (filter: AssetTableFilterOptions, value: string) => {
    navigateWithState(router, {
      to: Route.fullPath,
      search: (prev: Record<string, unknown>) => ({ ...prev, [filter]: value }),
    });
  };

  const handleOnCollectionGridFilterChange = (filter: CollectionGridFilterOptions, value?: string) => {
    navigateWithState(router, {
      to: Route.fullPath,
      search: (prev: Record<string, unknown>) => {
        const next = { ...prev };

        if (value) {
          next[filter] = value;
        } else {
          delete next[filter];
        }

        return next;
      },
    });
  };

  const handleOnBenchmarkGridFilterChange = (filter: BenchmarkGridFilterOptions, value?: string) => {
    navigateWithState(router, {
      to: Route.fullPath,
      search: (prev: Record<string, unknown>) => {
        const next = { ...prev };

        if (value) {
          next[filter] = value;
        } else {
          delete next[filter];
        }

        return next;
      },
    });
  };

  const handleOnCollectionsViewChange = (view: CollectionsViewOptions) => {
    navigateWithState(router, {
      to: Route.fullPath,
      search: (prev: Record<string, unknown>) => ({ ...prev, collectionsView: parseCollectionsView(view) }),
    });
  };

  return (
    <PagesProvider
      template={NavbarPageTemplate}
      templateProps={{ contentClassName: 'mx-0 max-w-none overflow-hidden' }}
    >
      <LibraryPage
        className="pt-7 max-sm:pt-8"
        selectedTab={search.view}
        productTableFilters={{
          [ProductTableFilterOptions.ListingScoreLevel]: search[ProductTableFilterOptions.ListingScoreLevel],
          [ProductTableFilterOptions.Brand]: search[ProductTableFilterOptions.Brand],
          [ProductTableFilterOptions.ProductCategory]: search[ProductTableFilterOptions.ProductCategory],
          [ProductTableFilterOptions.ProductCategoryName]: search[ProductTableFilterOptions.ProductCategoryName],
          [ProductTableFilterOptions.Search]: search[ProductTableFilterOptions.Search],
          [ProductTableFilterOptions.Retailer]: search[ProductTableFilterOptions.Retailer],
          [ProductTableFilterOptions.Experiment]: search[ProductTableFilterOptions.Experiment],
        }}
        assetTableFilters={{
          [AssetTableFilterOptions.ImageType]: search[AssetTableFilterOptions.ImageType],
          [AssetTableFilterOptions.ProductCategory]: search[AssetTableFilterOptions.ProductCategory],
          [AssetTableFilterOptions.ProductCategoryName]: search[AssetTableFilterOptions.ProductCategoryName],
        }}
        collectionGridFilters={{
          [CollectionGridFilterOptions.ImageType]: search[CollectionGridFilterOptions.ImageType],
          [CollectionGridFilterOptions.ProductCategory]: search[CollectionGridFilterOptions.ProductCategory],
          [CollectionGridFilterOptions.ProductCategoryName]: search[CollectionGridFilterOptions.ProductCategoryName],
          [CollectionGridFilterOptions.Search]: search[CollectionGridFilterOptions.Search],
          [CollectionGridFilterOptions.Sort]: search[CollectionGridFilterOptions.Sort],
        }}
        workspaceId={workspaceId ?? undefined}
        organizationId={organizationId || undefined}
        onTabChange={handleOnTabChange}
        onSuccessfulUpload={handleOnSuccessfulUpload}
        onSuccessfulImport={handleOnSuccessfulImport}
        onProductTableFilterChange={handleOnProductTableFilterChange}
        onAssetTableFilterChange={handleOnAssetTableFilterChange}
        onCollectionGridFilterChange={handleOnCollectionGridFilterChange}
        collectionsView={search.collectionsView}
        benchmarkGridFilters={{
          [BenchmarkGridFilterOptions.Search]: search[BenchmarkGridFilterOptions.Search],
          [BenchmarkGridFilterOptions.ProductCategory]: search[BenchmarkGridFilterOptions.ProductCategory],
          [BenchmarkGridFilterOptions.ProductCategoryName]: search[BenchmarkGridFilterOptions.ProductCategoryName],
          [BenchmarkGridFilterOptions.Sort]: search[BenchmarkGridFilterOptions.Sort],
        }}
        onBenchmarkGridFilterChange={handleOnBenchmarkGridFilterChange}
        onCollectionsViewChange={handleOnCollectionsViewChange}
      />
    </PagesProvider>
  );
}
